import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { KeyRound, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/staff/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { state: { resetSuccess: true } }), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
        <div className="max-w-md w-full fb-card p-6 sm:p-8 md:p-10 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--fb-error)" }} />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Invalid link</h1>
          <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>
            This reset link is missing a token. Please request a new one.
          </p>
          <Link to="/forgot-password" className="fb-btn-primary px-6 py-2.5 rounded-lg font-semibold inline-block">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
      <div className="max-w-md w-full fb-card p-6 sm:p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={44} />
          <div>
            <div className="font-bold" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
            <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Staff Portal</div>
          </div>
        </div>

        {!done ? (
          <>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Set new password</h1>
            <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>
              Choose a strong password of at least 8 characters.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">New password</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
                  <input
                    data-testid="reset-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    placeholder="At least 8 characters"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm password</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
                  <input
                    data-testid="reset-confirm-input"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your password"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 fb-status-error text-sm p-3 rounded-lg" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full fb-btn-primary py-3 rounded-lg font-semibold"
              >
                {loading ? "Saving…" : "Set new password"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="inline-flex items-center gap-1.5 font-medium underline"
                style={{ color: "var(--fb-text-muted)" }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center fb-status-success">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Password updated!</h2>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Your password has been changed. Redirecting you to sign in…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
